import { faArrowUpRight } from "@fortawesome/pro-regular-svg-icons/faArrowUpRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, Stack, useTheme } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcRequestErrorReport, MprcSpinnerIndicator, MprcTypography } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcHtmlViewer } from "components/MprcHtmlViewer";
import { MprcPdfViewer } from "components/MprcPdfViewer";
import { FeedAttachDto, FeedContentLoadStatus, FeedType, PublicationApprovalStatus } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import HtmlUtils from "lib/HtmlUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { InternalAttach } from "views/knowledge/components/card/behaviors/common/DefaultAttach";
import { CommentButton, FavoriteButton, LikeButton, ShareButton } from "views/knowledge/components/card/behaviors/common/Interaction";
import { StickerBox } from "views/knowledge/components/card/behaviors/common/StickerBox";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { CardContextLink } from "views/knowledge/components/card/CardContextLink";
import FeedItemHelper from "views/knowledge/components/card/FeedItemHelper";
import { ModeratorMessageButton } from "../../actions/ModeratorMessageDialog";
import { DefaultModerationFooter } from "../common/DefaultModerationFooter";
import { DefaultOptions } from "../common/DefaultOptions";

//
function isSinglePdf(helper: FeedItemHelper, attachs: FeedAttachDto[] | null, content: string | null, contentLoadStatus: FeedContentLoadStatus) {
    return attachs?.length == 1
        && helper.getAttach(attachs[0]).isPdf()
        && (content ?? "").length == 0
        && contentLoadStatus == FeedContentLoadStatus.NoActionNecessary;
}

//
function isSingleHtml(helper: FeedItemHelper, attachs: FeedAttachDto[] | null, content: string | null, contentLoadStatus: FeedContentLoadStatus) {
    return attachs?.length == 1
        && helper.getAttach(attachs[0]).isHtml()
        && (content ?? "").length == 0
        && contentLoadStatus == FeedContentLoadStatus.NoActionNecessary;
}

///
export const DefaultTitle = () => {
    const { pub, otherInfo, pub: { expired, availableInTheFuture } } = useCardContext();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");

    const isBlocked = pub.blocked?.blockedOn != undefined;

    const color = isBlocked
        ? theme.palette.warning.main
        : expired || availableInTheFuture
            ? theme.palette.muted.light
            : theme.palette.common.black;

    var borderStyle: React.CSSProperties | undefined = otherInfo?.feedType != FeedType.ContentPage && (pub.interaction.isNewPublication || pub.blocked?.firstAccessAfterBlock === true || pub.rejected?.firstAccessAfterRejected === true)
        ? { borderLeft: "4px solid", borderLeftColor: theme.palette.warning.main, paddingLeft: theme.spacing(1) }
        : undefined;

    const navigateUrl = KnowledgePaths.PublicationViewerResolver(pub.publicationId, pub.publicationType, pub.masterPublication?.publicationId);

    return (
        <div style={borderStyle}>
            {
                navigateUrl == document.location.pathname
                    ? (
                        <Box sx={{ color: color }}>
                            <MprcFeedTypography variant="title" component="div" noWrap={true} color={"inherit"}>
                                <span title={pub.title}>{pub.title}</span>
                            </MprcFeedTypography>
                        </Box>
                    )
                    : (
                        <CardContextLink
                            publicationId={pub.publicationId}
                            to={navigateUrl}
                        >
                            <Box sx={{ color: color }}>
                                <MprcFeedTypography variant="title" component="div" noWrap={true} color={"inherit"}>
                                    <span title={pub.title}>{pub.title}</span>
                                </MprcFeedTypography>
                            </Box>
                        </CardContextLink>
                    )
            }
            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
                {expired && <StickerBox name={t("Feed.Card.Common.ExpiredText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {availableInTheFuture && <StickerBox name={t("Feed.Card.Common.DisplayOnText", { date: Formaters.dayMonth(pub.viewPeriodStart) })} textColor={theme.palette.common.white} backgroundColor={theme.palette.muted.light} />}
                {pub.sticker && <StickerBox name={pub.sticker.name} textColor={pub.sticker.textColor} backgroundColor={pub.sticker.backgroundColor} />}
            </Stack>
        </div>
    );
};

export const PageContent = () => {
    const {
        pub: { attachs, content, contentLoadStatus },
        helper
    } = useCardContext();

    const isPdfViewer = isSinglePdf(helper, attachs, content, contentLoadStatus);
    if (isPdfViewer) {
        const url = helper.getAttach(attachs![0]).sourceUrl();
        return <PdfViewer url={url} />;
    }

    const isHtmlViewer = isSingleHtml(helper, attachs, content, contentLoadStatus);
    if (isHtmlViewer) {
        return <HtmlViewer assetId={attachs![0].fileId!} />;
    }

    return <DefaultContent />;
};

const HtmlViewer = ({ assetId }: { assetId: string; }) => {
    const divRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (divRef.current) {
            setHeight(divRef.current.getClientRects()[0].top);
            console.log(divRef.current.getClientRects()[0].top);
        }
    }, [divRef.current]);

    return (
        <Box
            id="htmlViewer"
            ref={divRef}
            height={height == 0 ? 0 : `calc(100vh - ${(height + 140)}px)`}
            sx={(theme) => ({
                border: "solid 4px",
                borderColor: theme.palette.paper.dark
            })}
        >
            {height > 0 &&
                <MprcHtmlViewer
                    assetId={assetId}
                />
            }
        </Box>
    );
};

const PdfViewer = ({ url }: { url: string; }) => {
    const divRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);
    const { uiCulture } = useSession();

    useEffect(() => {
        if (divRef.current) {
            setHeight(divRef.current.getClientRects()[0].top);
            console.log(divRef.current.getClientRects()[0].top);
        }
    }, [divRef.current]);

    return (
        <Box
            id="pdfViewer"
            ref={divRef}
            height={height == 0 ? 0 : `calc(100vh - ${(height + 140)}px)`}
            sx={(theme) => ({
                border: "solid 4px",
                borderColor: theme.palette.paper.dark
            })}
        >
            {height > 0 &&
                <MprcPdfViewer
                    url={url}
                    locale={uiCulture}
                />
            }
        </Box>
    );
};

export const PageAttach = () => {
    const { pub: { attachs, content, contentLoadStatus }, helper, pub, onMarkRead } = useCardContext();

    if (!attachs || attachs.length == 0) return null;

    const isHtmlViewer = isSingleHtml(helper, attachs, content, contentLoadStatus);
    if (isHtmlViewer) return null;

    const isPdfViewer = isSinglePdf(helper, attachs, content, contentLoadStatus);
    if (isPdfViewer) {
        const at0 = helper.getAttach(attachs[0]);
        return (
            <div
                className={AppGlobalClassKeys.clickable}
                onClick={(e) => HtmlUtils.openUrlInNewWindow(at0.sourceUrl(), attachs[0].attachId)}
            >
                <Grid container justifyContent="end" alignItems="center">
                    <Grid>
                        <MprcTypography noWrapRows={2} variant="captionHeader" color="black" fontWeight={"bold"}>
                            {at0.getTitle()}
                        </MprcTypography>
                    </Grid>
                    <Grid>
                        <FontAwesomeIcon icon={faArrowUpRight} fixedWidth />
                    </Grid>
                </Grid>
            </div >
        );
    }

    return <InternalAttach publicationId={pub.publicationId} attachs={attachs} helper={helper} onMarkRead={onMarkRead} />;
};

///
export const DefaultContent = () => {
    const {
        pub: { publicationId, content },
        localContent,
        onUpdateLocalContent,
    } = useCardContext();

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(undefined);
    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const loadContent = () => {
        setLoading(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                onUpdateLocalContent({
                    content: result.data,
                    contentLoadStatus: FeedContentLoadStatus.AllContentLoaded,
                    state: result.data != null ? "expanded" : undefined
                });

            })
            .catch((e) => setError(e))
            .finally(() => setLoading(false));
    };

    const toogleState = () => onUpdateLocalContent({ ...localContent, state: localContent.state == "expanded" ? "contracted" : "expanded" });

    return error
        ? <MprcRequestErrorReport error={error} />
        : (
            <section id="k-content">
                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.AllContentLoaded &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: localContent.state == "expanded" ? localContent.content! : content! }}></div>
                        </MprcFeedTypography>
                        <MprcFeedTypography variant="moreContent" component="a" onClick={toogleState}>
                            {localContent.state == "expanded" ? t("Feed.Card.Common.SeeLessText") : t("Feed.Card.Common.SeeMoreText")}
                        </MprcFeedTypography>
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.LoadContentInDemand &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div>
                        </MprcFeedTypography>
                        {
                            loading
                                ? <MprcSpinnerIndicator />
                                : <MprcFeedTypography variant="moreContent" component="a" onClick={() => loadContent()}>
                                    {t("Feed.Card.Common.SeeMoreText")}
                                </MprcFeedTypography>
                        }
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary && content &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content }}></div>
                        </MprcFeedTypography>
                    </>
                }
            </section>
        );
};

///
export const DefaultFooter = () => {

    const { pub: { blocked, approvalStatus } } = useCardContext();

    if (approvalStatus != PublicationApprovalStatus.Approved)
        return <DefaultModerationFooter />;

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid>
                        <LikeButton />
                    </Grid>
                    <Grid>
                        <CommentButton />
                    </Grid>
                </Grid>
            </Grid>
            {blocked?.blockedOn != null &&
                <Grid>
                    <ModeratorMessageButton optionMode="blocked" />
                </Grid>
            }
            <Grid>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid>
                        <FavoriteButton />
                    </Grid>
                    <Grid>
                        <ShareButton />
                    </Grid>
                    <Grid>
                        <DefaultOptions />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
